import React, { FC, ReactNode, useCallback, useContext } from 'react';
import Button from '@atlaskit/button/standard-button';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import { useQuery } from '@tanstack/react-query';
import ReactJson from 'react-json-view';
import Lozenge from '@atlaskit/lozenge';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';

import { querySpec } from '@/services/api-service/api-queries';
import { useFetchErrorController } from '@/controllers';
import { apiFetch } from '@/services';
import { Theme, ThemeContext } from '@/controllers/theme-controller';
import {
  alignItemsStart,
  extendWidth,
  flex,
  flexDirectionColumn,
  flexStart,
  spaceGapCompact,
  spaceGapCozy,
  spaceTopCompact,
  spaceTopCozy,
} from '@/styles';
import SearchPageSkeleton from '@/components/composed/search/search-results/search-page-skeleton';
import { FetchError } from '@/services/api-service/api-service';

import { debugFontSize, panelStyles } from './styles';
import DebugInfoTable from './debug-info-table';

type DebugModalProps = {
  isOpen: boolean;
  setIsOpen: (x: boolean) => void;
  id: string;
};

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
interface JsonObject {
  [key: string]: JsonValue;
}
interface JsonArray extends Array<JsonValue> {}

function isJsonObject(value: JsonValue): value is JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Return a copy of the JSON value with keys sorted
function sortedKeys(obj: JsonValue): JsonValue {
  if (Array.isArray(obj)) {
    return obj.map(sortedKeys);
  }
  if (isJsonObject(obj)) {
    const sortedObj: JsonObject = {};
    Object.keys(obj)
      .sort()
      .forEach(key => {
        sortedObj[key] = sortedKeys((obj as JsonObject)[key]);
      });
    return sortedObj;
  }

  return obj;
}

const Panel = ({ children }: { children: ReactNode }) => (
  <div css={[spaceTopCozy, extendWidth, panelStyles]}>{children}</div>
);

const DebugModal: FC<DebugModalProps> = ({ isOpen, setIsOpen, id }) => {
  const [theme] = useContext(ThemeContext);

  const handleClose = useCallback(
    (e: any) => {
      e.stopPropagation();
      setIsOpen(false);
    },
    [setIsOpen]
  );

  const { onError } = useFetchErrorController();
  const { queryKey: queryKeySync, path: pathSync } = querySpec('debug.slo.{id}', { id });
  const { data, isInitialLoading, error } = useQuery(queryKeySync, () => apiFetch(pathSync), {
    onError,
    enabled: isOpen,
  });

  const { slo, dailies, indicators, datapoints, audit, events } = data || {};
  const hasError: boolean = !isInitialLoading && error instanceof FetchError;

  if (!isOpen) return <></>;

  return (
    <ModalTransition>
      <Modal onClose={handleClose} width="100%">
        <ModalHeader>
          <ModalTitle>Debug Info</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {isInitialLoading && <SearchPageSkeleton isCompact />}
          {!isInitialLoading && hasError && <div>{error?.message}</div>}
          {!isInitialLoading && !hasError && (
            <div css={[flexDirectionColumn, flex, spaceGapCozy]}>
              <section>
                <div
                  css={[
                    spaceTopCompact,
                    debugFontSize,
                    flexStart,
                    alignItemsStart,
                    spaceGapCompact,
                  ]}
                >
                  <Lozenge appearance="default">slo</Lozenge>
                  <ReactJson
                    theme={theme === Theme.Dark ? 'google' : 'rjv-default'}
                    src={(isJsonObject(slo) ? sortedKeys(slo) : slo) || {}}
                    name={false}
                    displayObjectSize={false}
                    displayDataTypes={false}
                    enableClipboard={false}
                    iconStyle="square"
                    style={{ background: 'inherit' }}
                  />
                </div>
              </section>
              <section>
                <Tabs id="slo-debug-info-tabs">
                  <TabList>
                    <Tab>dailies</Tab>
                    <Tab>indicators</Tab>
                    <Tab>datapoints</Tab>
                    <Tab>audit</Tab>
                    <Tab>events</Tab>
                  </TabList>
                  <TabPanel>
                    <Panel>
                      <DebugInfoTable
                        data={dailies}
                        pickFields={[
                          'timestamp',
                          'good_events',
                          'bad_events',
                          'rolling_good_events',
                          'rolling_bad_events',
                        ]}
                      />
                    </Panel>
                  </TabPanel>
                  <TabPanel>
                    <Panel>
                      <DebugInfoTable
                        data={indicators}
                        pickFields={[
                          'id',
                          'public_id',
                          'good_query',
                          'bad_query',
                          'type',
                          'enabled',
                        ]}
                      />
                    </Panel>
                  </TabPanel>
                  <TabPanel>
                    <Panel>
                      <DebugInfoTable
                        data={datapoints}
                        pickFields={['good_events', 'bad_events']}
                      />
                    </Panel>
                  </TabPanel>
                  <TabPanel>
                    <Panel>
                      <DebugInfoTable data={audit} pickFields={['id', 'rev', 'revtype']} />
                    </Panel>
                  </TabPanel>
                  <TabPanel>
                    <Panel>
                      <DebugInfoTable
                        data={events}
                        pickFields={['id', 'indicator_id', 'impact_start', 'impact_end']}
                      />
                    </Panel>
                  </TabPanel>
                </Tabs>
              </section>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button appearance="primary" onClick={handleClose} autoFocus>
            Ok
          </Button>
        </ModalFooter>
      </Modal>
    </ModalTransition>
  );
};

export default DebugModal;
